"use client";

import { useAppName } from "@/features/theme/app-name-context";
import { signIn } from "next-auth/react";
import { FC, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

// Helper function to normalize email addresses for case-insensitive comparison
const normalizeEmail = (email: string): string => {
  return email.toLowerCase().trim();
};

interface LoginProps {
  isDevMode: boolean;
}

export const LogIn: FC<LoginProps> = (props) => {
  const appName = useAppName();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get("callbackUrl") || "/chat";
  const [username, setUsername] = useState("dev");
  const [email, setEmail] = useState("dev@allata.com");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isContractor, setIsContractor] = useState(false);

  useEffect(() => {
    if (!props.isDevMode) {
      void signIn("azure-ad", { callbackUrl });
    }
  });

  return (
    <Card className="flex gap-2 flex-col min-w-[300px]">
      <CardHeader className="gap-2">
        <CardTitle className="text-2xl flex gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={"apple-icon.png"} />
          </Avatar>
          <span className="text-primary">{appName}</span>
        </CardTitle>
        <CardDescription>
          Login in with your Microsoft 365 account
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <Button onClick={() => signIn("azure-ad", { callbackUrl })}> Microsoft 365</Button>
        
        {props.isDevMode ? (
          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium">Local Development Login</h3>
            
            <div className="grid gap-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>User Roles</Label>
              <div className="flex flex-col gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isAdmin"
                    checked={isAdmin}
                    onCheckedChange={(checked) => setIsAdmin(checked === true)}
                  />
                  <Label htmlFor="isAdmin" className="text-sm font-normal">Admin</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isEmployee"
                    checked={isEmployee}
                    onCheckedChange={(checked) => setIsEmployee(checked === true)}
                  />
                  <Label htmlFor="isEmployee" className="text-sm font-normal">Employee</Label>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isContractor"
                    checked={isContractor}
                    onCheckedChange={(checked) => setIsContractor(checked === true)}
                  />
                  <Label htmlFor="isContractor" className="text-sm font-normal">Contractor</Label>
                </div>
              </div>
            </div>
            
            <Button onClick={() => signIn("credentials", {
        redirect: true,
        callbackUrl: callbackUrl,
        // These are the credentials that will be passed to the authorize function
        username,
        email: normalizeEmail(email),
        isAdmin: isAdmin.toString(),
        isEmployee: isEmployee.toString(),
        isContractor: isContractor.toString(),
      })} className="w-full">
              Sign In (DEV ONLY)
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
